import { zodResolver } from '@hookform/resolvers/zod';
import { FormFloatField } from '@mdvip/shared/components/blocks/FormFloatField';
import { Icon } from '@mdvip/shared/components/blocks/Icon';
import {
  ResponsiveModal,
  ResponsiveModalBody,
  ResponsiveModalFooter,
  ResponsiveModalHeader,
  ResponsiveModalTitle,
} from '@mdvip/shared/components/blocks/ResponsiveModal';
import { Alert, AlertDescription } from '@mdvip/shared/components/ui/alert';
import { Button } from '@mdvip/shared/components/ui/button';
import { Checkbox } from '@mdvip/shared/components/ui/checkbox';
import { Field } from '@mdvip/shared/components/ui/field';
import { Label } from '@mdvip/shared/components/ui/label';
import { SubmitContactUsRequestError } from '@mdvip/shared/hooks/support/contactUsApi';
import { useContactUsCategories } from '@mdvip/shared/hooks/support/useContactUsCategories';
import { useSubmitContactUsRequest } from '@mdvip/shared/hooks/support/useSubmitContactUsRequest';
import { MDVIP_COMPANY_INFO } from '@mdvip/shared/lib/mdvip-company-info';
import {
  CONTACT_US_MESSAGE_MAX_LENGTH,
  CONTACT_US_VALIDATION_KEYS,
  contactUsSchema,
} from '@mdvip/shared/lib/support/contactUs.schema';
import { type ContactUsFormValues, isCancellationCategory } from '@mdvip/shared/lib/support/contactUs.types';
import { useEffect, useRef, useState } from 'react';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

interface ContactUsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Seeds "First & last name" — resolved by the caller, not fetched here (this modal is role-agnostic). */
  prefillName?: string;
  /** Seeds "Email address", same contract as `prefillName`. */
  prefillEmail?: string;
}

type Step = 'form' | 'cancelConfirm' | 'success';

function emptyValues(prefillName?: string, prefillEmail?: string): ContactUsFormValues {
  return {
    categoryKey: '',
    name: prefillName ?? '',
    email: prefillEmail ?? '',
    callbackNumber: '',
    message: '',
    agreedToPhiDisclaimer: false,
  };
}

/**
 * ContactUsModal — the member's support request: pick a category, give contact details and a
 * message, acknowledge the message carries no PHI, send.
 *
 * Three steps in one modal. Choosing the cancellation category inserts a confirmation step
 * carrying the membership-cancellation legal copy before anything is sent; every other category
 * goes straight from `form` to `success`. Which category that is comes from the server (see
 * `isCancellationCategory`), as does the per-category success copy — none of it is hardcoded here.
 *
 * Role-agnostic on purpose: it lives in `packages/shared` and takes the member's name/email as
 * props rather than fetching them, so the providers app can mount the same component later.
 */
export function ContactUsModal({ open, onOpenChange, prefillName, prefillEmail }: ContactUsModalProps) {
  const { t } = useTranslation('shared');
  // The schema stores i18n keys as messages, so bypass the strict typed-t the same way
  // `useTranslateError` does in the payments form. The phone goes in on every call because
  // `categoriesUnavailable` interpolates it and `FormFloatField` hands over only the key; keys that
  // take no values ignore it.
  const tMessage = t as unknown as (message: string, values?: Record<string, string>) => string;
  const translateError = (message: string) => tMessage(message, { phone: MDVIP_COMPANY_INFO.memberSupportPhone });

  const {
    data: categories = [],
    isLoading: isLoadingCategories,
    isError: isCategoriesError,
  } = useContactUsCategories();
  const { submit, isPending } = useSubmitContactUsRequest();

  const [step, setStep] = useState<Step>('form');

  const form = useForm<ContactUsFormValues>({
    resolver: zodResolver(contactUsSchema),
    defaultValues: emptyValues(prefillName, prefillEmail),
  });

  // Read through a ref so the reset below depends on `open` alone. Prefill comes from a query, so
  // it can resolve or refetch while the modal is already open; if it were a dependency, that would
  // re-run the reset and wipe whatever the member had typed.
  const prefillRef = useRef({ name: prefillName, email: prefillEmail });
  prefillRef.current = { name: prefillName, email: prefillEmail };

  // Fresh form on every open, so a previous submission's input never leaks into the next one.
  useEffect(() => {
    if (open) {
      setStep('form');
      form.reset(emptyValues(prefillRef.current.name, prefillRef.current.email));
    }
  }, [open, form]);

  // Prefill can land after the modal is already open, since the first visit to the page fetches it.
  // Fill a blank field when it does, and never overwrite one that already holds a value.
  useEffect(() => {
    if (!open) return;
    if (prefillName && !form.getValues('name')) form.setValue('name', prefillName);
    if (prefillEmail && !form.getValues('email')) form.setValue('email', prefillEmail);
  }, [open, prefillName, prefillEmail, form]);

  // Without categories the form cannot be completed at all, so the failure belongs on the field
  // rather than in a toast that disappears.
  useEffect(() => {
    if (open && isCategoriesError) {
      form.setError('categoryKey', { message: CONTACT_US_VALIDATION_KEYS.categoriesUnavailable });
    }
  }, [open, isCategoriesError, form]);

  const categoryKey = useWatch({ control: form.control, name: 'categoryKey' });
  const message = useWatch({ control: form.control, name: 'message' });
  const selectedCategory = categories.find((category) => category.key === categoryKey);
  const isCancellation = isCancellationCategory(selectedCategory);

  const send = async (values: ContactUsFormValues) => {
    try {
      await submit({
        categoryKey: values.categoryKey,
        name: values.name,
        email: values.email,
        callbackNumber: values.callbackNumber,
        message: values.message,
        agreedToPhiDisclaimer: values.agreedToPhiDisclaimer,
      });
      setStep('success');
    } catch (error) {
      // A category the server no longer accepts is a field problem, not a transient failure, so it
      // reads as an inline error under the dropdown; the hook has already refetched the list.
      // Everything else was toasted by the hook. Either way the form stays put with input intact.
      if (error instanceof SubmitContactUsRequestError && error.code === 'INVALID_CATEGORY') {
        form.setError('categoryKey', { message: CONTACT_US_VALIDATION_KEYS.categoryRequired });
      }
      setStep('form');
    }
  };

  const onSubmit = form.handleSubmit((values) => {
    if (isCancellation) {
      setStep('cancelConfirm');
      return;
    }
    void send(values);
  });

  const close = () => onOpenChange(false);

  if (step === 'cancelConfirm') {
    const phone = MDVIP_COMPANY_INFO.memberSupportPhone;
    return (
      <ResponsiveModal
        open={open}
        onOpenChange={onOpenChange}
        onBack={() => setStep('form')}
        backLabel={t('common.back')}
        className="sm:max-w-lg"
      >
        <ResponsiveModalHeader>
          <ResponsiveModalTitle>{t('contactUs.cancelConfirm.title')}</ResponsiveModalTitle>
        </ResponsiveModalHeader>
        <ResponsiveModalBody>
          <div className="flex flex-col gap-4 text-sm leading-relaxed text-foreground">
            <p>{t('contactUs.cancelConfirm.paragraph1')}</p>
            <p>
              <Trans
                t={t}
                i18nKey="contactUs.cancelConfirm.paragraph2"
                values={{ phone }}
                components={{
                  phone: (
                    // biome-ignore lint/a11y/useAnchorContent: Trans injects the phone number as this anchor's content
                    <a
                      href={`tel:${phone.replace(/\D/g, '')}`}
                      className="font-medium text-foreground underline decoration-from-font underline-offset-2 hover:text-muted-foreground"
                    />
                  ),
                }}
              />
            </p>
            <p>{t('contactUs.cancelConfirm.paragraph3')}</p>
          </div>
        </ResponsiveModalBody>
        <ResponsiveModalFooter>
          <Button type="button" variant="outline" onClick={() => setStep('form')}>
            {t('contactUs.cancelConfirm.goBack')}
          </Button>
          <Button type="button" variant="destructive" disabled={isPending} onClick={() => void send(form.getValues())}>
            {isPending ? (
              <>
                <Icon name="spinner" weight="solid" size="sm" className="animate-spin" /> {t('contactUs.submitting')}
              </>
            ) : (
              t('contactUs.cancelConfirm.confirm')
            )}
          </Button>
        </ResponsiveModalFooter>
      </ResponsiveModal>
    );
  }

  if (step === 'success') {
    return (
      <ResponsiveModal open={open} onOpenChange={onOpenChange} className="sm:max-w-md">
        <ResponsiveModalHeader>
          <ResponsiveModalTitle>
            {isCancellation ? t('contactUs.success.cancellationTitle') : t('contactUs.success.title')}
          </ResponsiveModalTitle>
        </ResponsiveModalHeader>
        <ResponsiveModalBody>
          <div className="flex flex-col items-center gap-3 py-4 text-center">
            <Icon name="circleCheck" weight="solid" className="text-[40px] text-success-foreground" />
            <div className="flex flex-col gap-3 text-sm leading-relaxed text-muted-foreground">
              <p>{t('contactUs.success.opening')}</p>
              {selectedCategory && <p>{selectedCategory.successMiddleLine}</p>}
              {selectedCategory?.cancellationSuccessLine && <p>{selectedCategory.cancellationSuccessLine}</p>}
            </div>
          </div>
        </ResponsiveModalBody>
        <ResponsiveModalFooter>
          <Button type="button" onClick={close}>
            {t('contactUs.success.done')}
          </Button>
        </ResponsiveModalFooter>
      </ResponsiveModal>
    );
  }

  const remaining = CONTACT_US_MESSAGE_MAX_LENGTH - message.length;
  const phiError = form.formState.errors.agreedToPhiDisclaimer?.message;

  return (
    <ResponsiveModal open={open} onOpenChange={onOpenChange} className="sm:max-w-2xl">
      <ResponsiveModalHeader>
        <ResponsiveModalTitle>{t('contactUs.title')}</ResponsiveModalTitle>
      </ResponsiveModalHeader>
      <ResponsiveModalBody>
        <FormProvider {...form}>
          <form id="contact-us-form" noValidate onSubmit={onSubmit} className="flex flex-col gap-5">
            <Alert variant="info">
              <Icon name="info" />
              <AlertDescription>
                <p>
                  <Trans
                    t={t}
                    i18nKey="contactUs.emergencyNotice"
                    components={{ bold: <span className="font-semibold text-foreground" /> }}
                  />
                </p>
              </AlertDescription>
            </Alert>

            <p className="text-xs text-muted-foreground">{t('contactUs.callbackHelper')}</p>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormFloatField<ContactUsFormValues>
                name="categoryKey"
                kind="select"
                label={t('contactUs.fields.category')}
                options={categories.map((category) => ({ value: category.key, label: category.label }))}
                disabled={isLoadingCategories || isCategoriesError}
                translateError={translateError}
              />
              <FormFloatField<ContactUsFormValues>
                name="name"
                label={t('contactUs.fields.name')}
                translateError={translateError}
              />
              <FormFloatField<ContactUsFormValues>
                name="email"
                label={t('contactUs.fields.email')}
                translateError={translateError}
              />
              <FormFloatField<ContactUsFormValues>
                name="callbackNumber"
                label={t('contactUs.fields.callbackNumber')}
                placeholder={t('contactUs.fields.callbackNumberPlaceholder')}
                inputMode="numeric"
                translateError={translateError}
              />
            </div>

            <Field>
              <div className="flex justify-end">
                {/* `polite` + `atomic` so the count is announced as one phrase once typing pauses,
                    rather than interrupting on every keystroke. */}
                <span aria-live="polite" aria-atomic="true" className="text-xs text-muted-foreground">
                  {t('contactUs.charactersRemaining', { remaining, max: CONTACT_US_MESSAGE_MAX_LENGTH })}
                </span>
              </div>
              <FormFloatField<ContactUsFormValues>
                name="message"
                kind="textarea"
                label={t('contactUs.fields.message')}
                rows={7}
                maxLength={CONTACT_US_MESSAGE_MAX_LENGTH}
                translateError={translateError}
              />
            </Field>

            <div className="flex flex-col gap-2">
              <div className="flex items-start gap-3">
                <Controller<ContactUsFormValues>
                  name="agreedToPhiDisclaimer"
                  render={({ field, fieldState }) => (
                    <Checkbox
                      id="contact-us-phi"
                      checked={field.value === true}
                      onCheckedChange={(checked) => field.onChange(checked === true)}
                      aria-invalid={fieldState.invalid || undefined}
                      aria-describedby={fieldState.invalid ? 'contact-us-phi-error' : undefined}
                      className="mt-0.5"
                    />
                  )}
                />
                <Label htmlFor="contact-us-phi" className="text-sm leading-relaxed font-normal text-foreground">
                  {t('contactUs.phiDisclaimer')}
                </Label>
              </div>
              {phiError && (
                <p id="contact-us-phi-error" role="alert" className="text-xs text-destructive-foreground">
                  {translateError(phiError)}
                </p>
              )}
            </div>
          </form>
        </FormProvider>
      </ResponsiveModalBody>
      <ResponsiveModalFooter>
        <Button type="button" variant="outline" onClick={close}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" form="contact-us-form" disabled={isPending}>
          {isPending ? (
            <>
              <Icon name="spinner" weight="solid" size="sm" className="animate-spin" /> {t('contactUs.submitting')}
            </>
          ) : (
            t('contactUs.submit')
          )}
        </Button>
      </ResponsiveModalFooter>
    </ResponsiveModal>
  );
}
